import Service, { service } from '@ember/service';
import { tracked } from '@glimmer/tracking';
import { task } from 'ember-concurrency';

import { HttpMethod } from 'later/utils/constants';
import { SegmentEventTypes } from 'later/utils/constants/segment-events';
import { fetch, objectToQueryString } from 'later/utils/fetch';
import { MavelyProfileIndex, type MavelyProfile } from 'later/utils/mavely-profile-index';
import { STRATEGY } from 'shared/utils/retry';

import type RouterService from '@ember/routing/router-service';
import type StoreService from '@ember-data/store';
import type IntlService from 'ember-intl/services/intl';
import type GroupModel from 'later/models/group';
import type SocialIdentityModel from 'later/models/social-identity';
import type AlertsService from 'later/services/alerts';
import type AuthService from 'later/services/auth';
import type DialogManagerService from 'later/services/dialog-manager';
import type ErrorsService from 'later/services/errors';
import type SegmentService from 'later/services/segment';
import type PopupInstance from 'shared/lib/popup-instance';
import type { PopupWindowFeatures } from 'shared/lib/popup-instance';
import type PopupManagerService from 'shared/services/shared/popup-manager';

export const MAVELY_AUTH_POPUP_NAME = 'mavely_oauth';

/**
 * Service to handle authenticating Mavely Account.
 */
export default class MavelyAuthService extends Service {
  @service declare alerts: AlertsService;
  @service declare auth: AuthService;
  @service declare dialogManager: DialogManagerService;
  @service declare errors: ErrorsService;
  @service declare intl: IntlService;
  @service declare router: RouterService;
  @service('shared/popup-manager') declare popupManager: PopupManagerService;
  @service declare store: StoreService;
  @service declare segment: SegmentService;

  /**
   * Stored by social identity ID, where Social Identity is the key.
   * Initialized in the constructor so `this.store` is DI-populated first —
   * TS refuses to see `this.store` as safe from a field initializer.
   */
  @tracked mavelyProfiles!: MavelyProfileIndex;

  constructor(owner: object) {
    super(owner);
    this.mavelyProfiles = new MavelyProfileIndex(this.store);
  }

  /**
   * Latest observed reachability of link-service, mirrored from the last
   * `/api/v2/mavely_profiles` response. False means link-service failed on the
   * BE this request — we soft-fail the profile list but must refuse to open a
   * new Mavely OAuth popup, since the token store is unreachable and any auth
   * attempt would leave state inconsistent.
   */
  @tracked linkServiceReachable = true;

  /**
   * Returns all connected profiles on current group and removes duplicates
   */
  get connectedMavelyProfilesOnCurrentGroup(): MavelyProfile[] {
    return this.mavelyProfiles
      .toArray()
      .filter((mavelyProfile) => mavelyProfile.groupIds.includes(this.currentGroup.id));
  }

  get currentGroup(): GroupModel {
    return this.auth.currentGroup;
  }

  get hasMavelyProfileOnCurrentGroup(): boolean {
    return Boolean(this.connectedMavelyProfilesOnCurrentGroup.length);
  }

  /**
   * Handles oAuth flow including creating popup and receiving mavely profile id
   */
  authenticate = task(async (socialIdentity?: SocialIdentityModel) => {
    const hasSocialIdentity = !!socialIdentity;
    const profileId = await this.handleAuthenticationPopup.perform(socialIdentity);

    if (!hasSocialIdentity) {
      //Note: if the user did not have any on the current group, backend had to create a social identity
      await this.store.findAll('social-identity');
    }

    if (hasSocialIdentity) {
      //Note: the endpoint does not return the profile info, only the ID, so we need to fetch the profile after completion
      await this.fetchProfiles.perform(socialIdentity);
    } else {
      await this.fetchProfilesOnGroup.perform(this.currentGroup);
    }
    return profileId;
  });

  disconnect = task(
    async ({
      socialIdentity,
      group,
      profile,
      area
    }: {
      socialIdentity: SocialIdentityModel;
      group: GroupModel;
      profile: MavelyProfile;
      area?: string;
    }) => {
      const didConfirm = await this.dialogManager.confirmation(
        this.intl.t('account.mavely.disconnect.confirm.title', { group: group.name }),
        {
          description: this.intl.t('account.mavely.disconnect.confirm.description', {
            account: profile.email,
            group: group.name
          }),
          confirmButton: this.intl.t('shared_words.disconnect'),
          cancelButton: this.intl.t('shared_words.cancel')
        }
      );

      if (!didConfirm) {
        return;
      }

      try {
        if (profile.connectionId != undefined) {
          // Preferred: RESTful DELETE by connection id (owner-only)
          await fetch(`api/v2/mavely_profiles/${profile.connectionId}`, {
            method: HttpMethod.Delete
          });
        } else {
          // Legacy: for accounts with no connection yet (connection_id: null) during the transition window
          const queryParams = { profile_id: profile.id, social_identity_id: socialIdentity.get('id') };
          await fetch(`api/v2/mavely_profiles/disconnect${objectToQueryString(queryParams)}`, {
            method: HttpMethod.Delete
          });
        }

        const profileEmail = profile.email;
        const numberOfProfiles = this.connectedMavelyProfilesOnCurrentGroup.length;

        this.mavelyProfiles.remove(profile.id);

        this.segment.track(SegmentEventTypes.AuthenticatedMavely, {
          group: group.id,
          area: area || '',
          number_of_profiles: numberOfProfiles,
          profile_email: profileEmail || '',
          type: 'disconnect'
        });

        this.alerts.success(
          this.intl.t('account.mavely.disconnect.success.description', {
            account: profile.email
          }),
          {
            title: this.intl.t('account.mavely.disconnect.success.title')
          }
        );
      } catch {
        this.alerts.alert(this.intl.t('shared_phrases.something_went_wrong'), {
          title: this.intl.t('account.mavely.disconnect.failure.title', { account: profile.email })
        });
      }
    }
  );

  fetchProfiles = task(async (socialIdentity: SocialIdentityModel) => {
    try {
      const url = `/api/v2/mavely_profiles${objectToQueryString({ social_identity_id: socialIdentity.get('id') })}`;
      const { data, link_service_reachable: reachable } = await fetch(
        url,
        {},
        { intl: null, numRetries: 1, retryStrategy: STRATEGY.DEFAULT, raw: false }
      );

      // BE returns `link_service_reachable: false` when it soft-failed the
      // upstream profile fetch. Notify once on the transition to false so the
      // user learns why the picker/blocks look empty; suppress repeat toasts on
      // subsequent polls. OAuth gating is enforced separately in
      // handleAuthenticationPopup.
      if (reachable === false && this.linkServiceReachable) {
        this.errors.show(new Error(this.intl.t('linkinbio.mavely.link_service_unavailable')));
      }
      this.linkServiceReachable = reachable !== false;

      // Sync semantic: clear this SI's slice of the index before re-adding.
      // Otherwise an empty response (e.g. after the SI's MC was moved away)
      // leaves the pre-move entry stale until a full page reload.
      this.mavelyProfiles.remove(socialIdentity);
      if (data) {
        this.mavelyProfiles.add(socialIdentity, data);
      }
    } catch (error) {
      this.alerts.alert(this.intl.t('shared_phrases.something_went_wrong'), {
        title: this.intl.t('alerts.linkinbio.blocks.mavely.connection.failure.title')
      });
      this.errors.log(error);
    }
  });

  /**
   * GET /api/v2/mavely_profiles is social_identity_id-scoped — group_id is rejected.
   * Iterates the group's SIs (filtered from the store, so a newly-created placeholder
   * SI is picked up even before the group's async hasMany inverse updates) and fetches
   * Mavely profiles per identity.
   */
  fetchProfilesOnGroup = task(async (group: GroupModel) => {
    const groupId = group.get('id');
    const identities = this.store
      .peekAll('social-identity')
      .filter((si) => Boolean(si.id) && si.belongsTo('group').id() === groupId);
    await Promise.all(identities.map((si) => this.fetchProfiles.perform(si)));
  });

  /**
   * After a social profile is added, the BE may have moved a Mavely id off a placeholder
   * ("ghost") SI onto the new real SI and deleted the placeholder atomically. Re-fetch
   * identities + Mavely state so the FE reflects the new server-side reality. Callers
   * must not cache/reuse the previous placeholder SI id after this runs.
   *
   * Best-effort: swallows errors so a refresh failure never rolls back the caller's
   * successful profile save.
   */
  refreshAfterProfileAdd = task(async () => {
    try {
      await this.store.findAll('social-identity');
      await this.fetchProfilesOnGroup.perform(this.currentGroup);
    } catch (error) {
      this.errors.log(error);
    }
  });

  handleAuthenticationPopup = task(async (socialIdentity?: SocialIdentityModel) => {
    // Guard: link-service brokers every write between Later, Mavely, and CDS.
    // If it's unreachable, kicking off OAuth would successfully mint a Mavely
    // token that we can't persist — leaving the user stuck with a live vendor
    // session and no local MC. Refuse up front and tell the user.
    if (!this.linkServiceReachable) {
      this.errors.show(new Error(this.intl.t('linkinbio.mavely.link_service_unavailable')));
      return undefined;
    }

    const queryParams = {
      redirect_path: this.router.urlFor('user.profile-refresh-popup', MAVELY_AUTH_POPUP_NAME).slice(1),
      authorizing_object_id: this.currentGroup.get('id'),
      authorizing_object_type: 'Group'
    };

    if (socialIdentity) {
      queryParams.authorizing_object_type = 'SocialIdentity';
      queryParams.authorizing_object_id = socialIdentity.get('id');
    }

    const popup = this.#makePopupWindow(MAVELY_AUTH_POPUP_NAME, `/oauth/mavely${objectToQueryString(queryParams)}`);
    const parentWidth = window.innerWidth;
    const parentHeight = window.innerHeight;
    const windowFeatures = {
      popup: true,
      width: 800,
      height: 600,
      left: (parentWidth - 800) / 2,
      top: (parentHeight - 600) / 2
    } satisfies PopupWindowFeatures;
    let data: { mavely_profile_id?: string; mavely_move_completed?: string; moved_to_si_id?: string } | undefined;
    try {
      // PopupInstance validates event.origin === window.location.origin AND
      // event.source === the popup window we opened before resolving open()
      // (see lib/shared/addon/lib/popup-instance.ts). Cross-origin frames and
      // same-origin browser extensions cannot forge a mavely_move_completed
      // payload.
      data = await popup.open({ features: windowFeatures });
    } catch (error) {
      // Popup closed without a success payload (user cancelled, 422 target-taken,
      // etc). Resync the group's mavelyProfiles so any in-flight FE state changes
      // during the flow reconcile back to what the BE actually holds.
      try {
        await this.fetchProfilesOnGroup.perform(this.currentGroup);
      } catch (refreshError) {
        this.errors.log(refreshError);
      }
      throw error;
    }

    if (data?.mavely_move_completed === '1') {
      const targetSet = this.auth.socialSets.find((si) => si.id === data.moved_to_si_id);
      const targetName = targetSet?.displayName || this.intl.t('shared_phrases.social_set');
      this.alerts.success(this.intl.t('alerts.mavely.move_success.message', { socialSetName: targetName }), {
        title: this.intl.t('alerts.mavely.move_success.title')
      });
      // authenticate() only refetches profiles for the target SI; the source
      // SI's stale entry in mavelyProfiles keeps its badge lit until we sweep
      // the whole group.
      await this.fetchProfilesOnGroup.perform(this.currentGroup);
    }

    return data?.mavely_profile_id;
  });

  #makePopupWindow(
    name: string,
    url: string
  ): PopupInstance<{ mavely_profile_id?: string; mavely_move_completed?: string; moved_to_si_id?: string }, true> {
    return this.popupManager.createPopup(name, { url });
  }
}

declare module '@ember/service' {
  interface Registry {
    'mavely-auth': MavelyAuthService;
  }
}
